import { useEffect, useMemo, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { mdToHtml } from '@/lib/markdown'
import type { FaqEntry, FaqPageData } from '@/types/faq'

/*
  FAQ page — database-driven from the IIA modules/website/faq table.
  Mirrors the glossary accordion (Alfarn title, Poppins rows, +/− toggle)
  but as a flat list (no categories):
    - Row label = the question (plain text — markdown block tags can't
      nest inside a <button>).
    - Expanded panel = the answer rendered from markdown (mdToHtml).
    - Rows collapse by default; multiple can be open at once.
    - BACK button above the footer.
  Ordered by sort_order (numeric, blanks last) then question A–Z.

  Styling lives in src/styles/pages/faq.css.
*/

function PlusMinusIcon({ open }: { open: boolean }) {
  return (
    <span aria-hidden="true" className="mig-faq-row-icon" data-open={open}>
      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
        <rect x="0" y="10" width="24" height="4" />
        {!open && <rect x="10" y="0" width="4" height="24" />}
      </svg>
    </span>
  )
}

function FaqRow({
  entry,
  rowId,
  open,
  onToggle,
}: {
  entry: FaqEntry
  rowId: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="mig-faq-item" id={`faq-${rowId}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`faq-panel-${rowId}`}
        className="mig-faq-row"
      >
        <span className="mig-faq-row-question">{entry.question}</span>
        <PlusMinusIcon open={open} />
      </button>

      {open && entry.answer && (
        <div
          id={`faq-panel-${rowId}`}
          className="mig-faq-panel mig-faq-answer"
          dangerouslySetInnerHTML={{ __html: mdToHtml(entry.answer) }}
        />
      )}
    </div>
  )
}

/* Stable per-row identifier: prefer the slug, fall back to the record id
   so a blank slug can never collapse the anchor/key to undefined. */
function rowIdFor(entry: FaqEntry): string {
  const slug = (entry.slug ?? '').trim()
  return slug || String(entry.id)
}

/* Numeric order with blanks/nulls last, then question A–Z. Defensive
   against the API returning sort_order as a string or the property sort
   being lexicographic. */
function sortEntries(entries: FaqEntry[]): FaqEntry[] {
  const order = (e: FaqEntry): number => {
    if (e.sort_order == null || e.sort_order === '') return Infinity
    const n = Number(e.sort_order)
    return Number.isNaN(n) ? Infinity : n
  }
  return [...entries].sort(
    (a, b) => order(a) - order(b) || (a.question || '').localeCompare(b.question || ''),
  )
}

export default function Faq() {
  const data = usePageData<FaqPageData>()
  const entries = useMemo(() => sortEntries(data?.entries ?? []), [data?.entries])

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Multiple rows can be open at once; all collapsed on load.
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // BACK returns to the same-origin referrer when there is one (the
  // footer links here from every page), else home.
  const backHref = (() => {
    try {
      const ref = document.referrer
      if (ref) {
        const url = new URL(ref)
        if (url.origin === window.location.origin && url.pathname !== '/faq') {
          return url.pathname
        }
      }
    } catch { /* ignore */ }
    return '/'
  })()

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-faq-page-title">FAQs</h1>
      </div>

      <div className="mig-faq-content">
        {entries.length === 0 ? (
          <p className="mig-faq-empty">No FAQs yet. Check back soon.</p>
        ) : (
          <div className="mig-faq-accordion">
            {entries.map((entry) => {
              const rowId = rowIdFor(entry)
              return (
                <FaqRow
                  key={rowId}
                  rowId={rowId}
                  entry={entry}
                  open={openIds.has(rowId)}
                  onToggle={() => toggle(rowId)}
                />
              )
            })}
          </div>
        )}

        {/* BACK button */}
        <div className="mig-page-back-row">
          <a href={backHref} className="mig-faq-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}
